import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Cpu, Shield, Cloud, Zap, X, AlertCircle, Send, Clock, Check, Smartphone, MapPin, MessageCircle, Globe, Layout, Server } from 'lucide-react';

const Hero = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [popupVisible, setPopupVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(30); // 30 seconds countdown
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-close popup after 8 seconds
  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        handleClosePopup();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  // Countdown timer
  useEffect(() => {
    if (showPopup && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showPopup, timeLeft]);

  const handleClosePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setPopupVisible(false);
      setShowPopup(false);
    }, 300);
  };

  const scrollToSection = (e, href) => {
    e.preventDefault();
    handleClosePopup();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi! I'm interested in the R350/month website promo. Please send me more information.");
    window.open(`https://wa.me/27725939799?text=${message}`, '_blank');
    handleClosePopup();
  };

  const features = [
    { icon: Layout, text: 'Professional Business Website' },
    { icon: Smartphone, text: 'Mobile Friendly Design' },
    { icon: Server, text: 'Free Hosting' },
    { icon: Send, text: 'Contact Form for Enquiries' },
    { icon: MapPin, text: 'Google Maps Location' },
    { icon: MessageCircle, text: 'WhatsApp Chat Integration' },
    { icon: Globe, text: 'SEO Setup' },
  ];

  const industries = [
    'Small Businesses', 'Construction Companies', 'Cleaning Services',
    'Salons & Barbershops', 'Catering Businesses', 'Consultants & Professionals', 'Startups'
  ];

  return (
    <>
      {/* Promo Popup Modal */}
      {popupVisible && showPopup && (
        <div 
          className={`fixed inset-0 z-[1000] flex items-center justify-center p-4 transition-all duration-300 ${
            isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
          }`}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
        >
          <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-popup">
            {/* Close Button */}
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-300 z-10"
            >
              <X size={18} className="text-gray-600" />
            </button>

            {/* Countdown Timer Bar */}
            <div className="relative h-1 bg-gray-200 rounded-t-2xl overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-logo-green to-logo-yellow transition-all duration-1000"
                style={{ width: `${(timeLeft / 30) * 100}%` }}
              ></div>
            </div>

            <div className="p-6 sm:p-8">
              {/* Header with Alert Icon */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center animate-pulse">
                  <AlertCircle className="text-white" size={24} />
                </div>
                <div>
                  <div className="inline-flex items-center space-x-2 bg-red-100 text-red-600 px-3 py-1 rounded-full">
                    <span className="text-xs font-bold">🚨 LIMITED TIME OFFER</span>
                  </div>
                </div>
              </div>

              {/* Main Title */}
              <h3 className="text-2xl sm:text-3xl font-bold text-logo-navy mb-3">
                SMALL BUSINESS OWNERS – GET A PROFESSIONAL WEBSITE FOR ONLY{' '}
                <span className="text-3xl sm:text-4xl bg-gradient-to-r from-logo-green to-logo-yellow bg-clip-text text-transparent">
                  R350/MONTH!
                </span>
              </h3>

              <p className="text-gray-600 mb-4 leading-relaxed">
                Most businesses lose customers because <span className="font-bold text-red-600">they don't have a website</span> or their business <span className="font-bold text-red-600">cannot be found online</span>.
              </p>

              <p className="text-gray-700 mb-4 font-semibold">
                Now you can get a <span className="text-logo-darkblue">modern business website</span> without paying thousands upfront.
              </p>

              {/* Fire Emoji Decoration */}
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-2xl">🔥</span>
                <span className="font-bold text-logo-navy">LIMITED PROMO – ONLY R350 PER MONTH</span>
                <span className="text-2xl">🔥</span>
              </div>

              {/* Features Grid */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4 mb-4">
                <h4 className="font-bold text-logo-navy mb-3">Your website will include:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <feature.icon className="text-logo-green" size={16} />
                      <span className="text-sm text-gray-700">✅ {feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Industries Served */}
              <div className="mb-4">
                <h4 className="font-bold text-logo-navy mb-2">Perfect for:</h4>
                <div className="flex flex-wrap gap-2">
                  {industries.map((industry, index) => (
                    <span key={index} className="bg-gray-100 px-3 py-1 rounded-full text-xs text-gray-600">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-r from-logo-darkblue/10 to-logo-lightblue/10 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Check className="text-logo-green" size={16} />
                    <span className="text-sm text-gray-700">💡 No large upfront cost</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="text-logo-green" size={16} />
                    <span className="text-sm text-gray-700">💡 Quick setup</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="text-logo-green" size={16} />
                    <span className="text-sm text-gray-700">💡 Perfect for businesses that want to grow online</span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="text-red-500" size={18} />
                  <span className="text-sm text-red-700 font-medium">
                    ⚠️ Only accepting 30 businesses for this promo.
                  </span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleWhatsAppClick}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-6 rounded-lg
                           hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center justify-center space-x-2
                           transform hover:scale-105"
                >
                  <MessageCircle size={20} />
                  <span>📩 Send WhatsApp Message</span>
                </button>
                <button
                  onClick={(e) => scrollToSection(e, '#contact')}
                  className="flex-1 bg-gradient-to-r from-logo-darkblue to-logo-lightblue text-white font-bold py-3 px-6 rounded-lg
                           hover:opacity-90 transition-all duration-300 flex items-center justify-center space-x-2
                           transform hover:scale-105"
                >
                  <Send size={20} />
                  <span>Contact Us Now</span>
                </button>
              </div>

              {/* Countdown Text */}
              <div className="text-center mt-4">
                <p className="text-xs text-gray-400">
                  This popup will close in <span className="font-bold text-logo-darkblue">{timeLeft}</span> seconds
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Animated Background - Optimized for mobile */}
        <div className="absolute inset-0 bg-gradient-to-br from-logo-navy via-logo-darkblue to-logo-lightblue">
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          
          {/* Animated blobs - Hidden on mobile for performance */}
          {!isMobile && (
            <>
              <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-logo-lightblue/20 rounded-full blur-3xl animate-pulse-glow"></div>
              <div className="absolute top-1/3 left-1/2 w-64 h-64 bg-logo-green/10 rounded-full blur-3xl animate-pulse"></div>
            </>
          )}
          
          {/* Mobile-optimized background elements */}
          {isMobile && (
            <>
              <div className="absolute top-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-20 left-10 w-48 h-48 bg-logo-lightblue/15 rounded-full blur-2xl"></div>
            </>
          )}
          
          {/* Tech Pattern Overlay - Lighter on mobile */}
          <div className={`tech-pattern ${isMobile ? 'opacity-5' : 'opacity-10'}`}></div>
          
          {/* Floating Elements - Hidden on mobile for better performance */}
          {!isMobile && (
            <>
              <div className="absolute top-1/4 left-1/4 animate-float">
                <Cpu className="text-white/20 w-16 h-16" />
              </div>
              <div className="absolute bottom-1/3 right-1/3 animate-float" style={{ animationDelay: '1s' }}>
                <Shield className="text-white/20 w-20 h-20" />
              </div>
              <div className="absolute top-2/3 left-2/3 animate-float" style={{ animationDelay: '2s' }}>
                <Cloud className="text-white/20 w-24 h-24" />
              </div>
            </>
          )}
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Left Content */}
            <div className="lg:w-1/2 text-white text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6">
                Strategic IT Leadership for{' '}
                <span className="bg-gradient-to-r from-logo-green to-logo-yellow bg-clip-text text-transparent">
                  Growing Businesses
                </span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-6 sm:mb-8 max-w-lg mx-auto lg:mx-0">
                Stop putting out fires. Start driving growth. Transform technology into a business accelerator.
              </p>

              {/* CTA Buttons - Stack on mobile, row on desktop */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mb-8 sm:mb-12 justify-center lg:justify-start">
                <a
                  href="#contact"
                  onClick={(e) => scrollToSection(e, '#contact')}
                  className="btn-accent group inline-flex items-center justify-center space-x-2 text-sm sm:text-base px-5 py-2.5 sm:px-6 sm:py-3"
                >
                  <span>Book a Consultation</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="#services"
                  onClick={(e) => scrollToSection(e, '#services')}
                  className="btn-secondary group inline-flex items-center justify-center space-x-2 text-sm sm:text-base px-5 py-2.5 sm:px-6 sm:py-3"
                >
                  <span>Our Services</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Trust Indicators - Responsive sizing */}
              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 justify-center lg:justify-start">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-logo-green2 to-logo-green3 
                                          border-2 border-white flex items-center justify-center text-xs text-white font-bold">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div className="text-xs sm:text-sm text-blue-200">
                  <span className="font-bold text-white">50+</span> businesses transformed
                </div>
                <div className="text-xs sm:text-sm text-blue-200">
                  <span className="font-bold text-white">24/7</span> Support Available
                </div>
              </div>
            </div>

            {/* Right Content - Feature Cards */}
            <div className="lg:w-1/2 w-full mt-8 lg:mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Card 1 */}
                <div className="glass-effect rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-105 transition-all duration-300
                              backdrop-blur-lg border border-white/20">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-logo-green to-logo-green2 rounded-lg 
                                flex items-center justify-center mb-3 sm:mb-4">
                    <CheckCircle className="text-white" size={isMobile ? 20 : 24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">Executive Strategy</h3>
                  <p className="text-blue-100 text-xs sm:text-sm">CTO-level guidance aligned with your business goals</p>
                </div>

                {/* Card 2 - With translate on desktop only */}
                <div className={`glass-effect rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-105 transition-all duration-300
                              backdrop-blur-lg border border-white/20 ${!isMobile ? 'lg:translate-y-4' : ''}`}>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-logo-lightblue to-logo-teal rounded-lg 
                                flex items-center justify-center mb-3 sm:mb-4">
                    <Shield className="text-white" size={isMobile ? 20 : 24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">Proactive Security</h3>
                  <p className="text-blue-100 text-xs sm:text-sm">Enterprise-grade protection and compliance</p>
                </div>

                {/* Card 3 */}
                <div className="glass-effect rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-105 transition-all duration-300
                              backdrop-blur-lg border border-white/20">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-logo-yellow to-logo-green rounded-lg 
                                flex items-center justify-center mb-3 sm:mb-4">
                    <Cloud className="text-logo-navy" size={isMobile ? 20 : 24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">Scalable Cloud</h3>
                  <p className="text-blue-100 text-xs sm:text-sm">AWS, Azure, GCP optimized infrastructure</p>
                </div>

                {/* Card 4 - With translate on desktop only */}
                <div className={`glass-effect rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-105 transition-all duration-300
                              backdrop-blur-lg border border-white/20 ${!isMobile ? 'lg:translate-y-4' : ''}`}>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-logo-teal to-logo-green3 rounded-lg 
                                flex items-center justify-center mb-3 sm:mb-4">
                    <Zap className="text-white" size={isMobile ? 20 : 24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">Rapid Innovation</h3>
                  <p className="text-blue-100 text-xs sm:text-sm">Accelerate your digital transformation</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Responsive positioning */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2">
          <a
            href="#about"
            onClick={(e) => scrollToSection(e, '#about')}
            className="flex flex-col items-center space-y-1 sm:space-y-2 text-white/60 hover:text-white transition-colors"
          >
            <span className="text-xs sm:text-sm">Scroll</span>
            <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center
                          hover:border-white transition-colors">
              <div className="w-1 h-1.5 sm:w-1.5 sm:h-2 bg-white rounded-full mt-1.5 sm:mt-2 animate-bounce"></div>
            </div>
          </a>
        </div>
      </section>
    </>
  );
};

export default Hero;